import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Sparkles, X, Loader2, ArrowLeft, Users, Phone, ChevronDown, ChevronUp,
  BarChart3, TrendingUp,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const _fmtCcy = (v) => {
  if (!v) return '£0';
  return `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const _fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return iso.slice(0, 10) || ''; }
};

const _statusBadge = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'completed') return 'bg-green-100 text-green-700';
  if (s === 'scheduled') return 'bg-blue-100 text-blue-700';
  return 'bg-gray-100 text-gray-600';
};

// ─── Phase 1: form ─────────────────────────────────────────────────────────
function FormPhase({
  reportName, setReportName, setNameTouched,
  periodLabel, prevPeriodLabel, phocasRowCount, callCount,
  callsOnly,
  generating, onBack, onNext,
}) {
  return (
    <>
      {/* Period / data summary */}
      <div className="mt-2 rounded-xl border border-sky-200 bg-sky-50/60 p-3 space-y-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-sky-700" />
          <span className="text-xs font-bold uppercase tracking-wider text-sky-700">Report data included</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="text-center rounded-lg bg-white border border-sky-100 py-2 px-1">
            <div className="text-lg font-extrabold text-gray-900">{callCount}</div>
            <div className="text-[10px] uppercase tracking-wider text-sky-700 font-bold">CRM Calls</div>
          </div>
          <div className="text-center rounded-lg bg-white border border-sky-100 py-2 px-1">
            <div className="text-xs font-bold text-gray-900">{periodLabel || '—'}</div>
            {prevPeriodLabel && <div className="text-[10px] text-gray-400">vs {prevPeriodLabel}</div>}
            <div className="text-[10px] uppercase tracking-wider text-sky-700 font-bold mt-0.5">Phocas Period</div>
          </div>
          <div className="text-center rounded-lg bg-white border border-sky-100 py-2 px-1">
            <div className="text-lg font-extrabold text-gray-900">{phocasRowCount || 0}</div>
            <div className="text-[10px] uppercase tracking-wider text-sky-700 font-bold">Phocas Accts</div>
          </div>
        </div>
        {!phocasRowCount && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
            No Phocas data loaded — report will include calls only, without sales figures. Upload Phocas data first for full financial analysis.
          </p>
        )}
        {/* Data mode indicator */}
        <div className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 border text-xs font-semibold ${
          callsOnly
            ? 'bg-gray-50 border-gray-200 text-gray-600'
            : 'bg-sky-50 border-sky-200 text-sky-700'
        }`}>
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${callsOnly ? 'bg-gray-400' : 'bg-sky-500'}`} />
          {callsOnly
            ? 'Calls Only mode — report will include customers with CRM calls only, sorted by call count'
            : 'All Phocas Data mode — report includes all Phocas accounts, sorted by growth / decline'}
        </div>
      </div>

      {/* Report name */}
      <div className="mt-4">
        <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Report name</label>
        <input
          type="text"
          value={reportName}
          onChange={(e) => { setReportName(e.target.value); setNameTouched(true); }}
          disabled={generating}
          maxLength={120}
          className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-sky-500 outline-none text-sm"
          data-testid="crm-report-name-input"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-5 justify-end">
        <Button variant="ghost" onClick={onBack} disabled={generating}>Cancel</Button>
        <Button
          onClick={onNext}
          disabled={generating || callCount === 0}
          className="text-white"
          style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}
          data-testid="crm-report-next-btn"
        >
          {generating
            ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Loading…</>
            : <><Sparkles className="w-4 h-4 mr-1" />Select calls to include</>
          }
        </Button>
      </div>
      <p className="text-[11px] text-gray-500 mt-2 italic">Report saved to your account.</p>
    </>
  );
}

// ─── Phase 2: call selection ───────────────────────────────────────────────
function CallSelectionPhase({ calls, excludedIds, setExcludedIds, periodLabel, generating, onBack, onGenerate }) {
  const [collapsed, setCollapsed] = useState({});

  const groups = useMemo(() => {
    const map = {};
    calls.forEach(c => {
      const owner = c.owner_name || 'Unknown';
      if (!map[owner]) map[owner] = [];
      map[owner].push(c);
    });
    return Object.entries(map).sort((a, b) => b[1].length - a[1].length);
  }, [calls]);

  const totalSelected = calls.length - excludedIds.size;

  const toggleCall = (uid) => {
    setExcludedIds(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const toggleOwner = (ownerCalls) => {
    const uids = ownerCalls.map(c => c.id);
    const allChecked = uids.every(id => !excludedIds.has(id));
    setExcludedIds(prev => {
      const next = new Set(prev);
      uids.forEach(id => allChecked ? next.add(id) : next.delete(id));
      return next;
    });
  };

  const toggleAll = () => {
    if (excludedIds.size === 0) {
      setExcludedIds(new Set(calls.map(c => c.id)));
    } else {
      setExcludedIds(new Set());
    }
  };

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <div>
          <p className="text-sm text-gray-600">
            <span className="font-semibold text-gray-900">{periodLabel || 'Current period'}</span>
            {' · '}
            <span className="font-semibold text-sky-600">{totalSelected}</span>
            <span className="text-gray-500"> / {calls.length} calls selected</span>
          </p>
          <p className="text-xs text-gray-400 mt-0.5">Uncheck any calls to exclude them from the report.</p>
        </div>
        <button
          onClick={toggleAll}
          className="text-xs font-semibold text-sky-600 hover:underline px-2 py-1 rounded"
          data-testid="crm-select-toggle-all"
        >
          {excludedIds.size === 0 ? 'Deselect all' : 'Select all'}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto border rounded-xl divide-y divide-gray-100 bg-white min-h-0" style={{ maxHeight: '380px' }}>
        {groups.map(([owner, ownerCalls]) => {
          const ownerExcluded = ownerCalls.filter(c => excludedIds.has(c.id)).length;
          const allOwnerChecked = ownerExcluded === 0;
          const someChecked = ownerExcluded > 0 && ownerExcluded < ownerCalls.length;
          const isCollapsed = collapsed[owner];

          return (
            <div key={owner}>
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 sticky top-0 z-10 border-b">
                <Checkbox
                  checked={allOwnerChecked}
                  data-state={someChecked ? 'indeterminate' : allOwnerChecked ? 'checked' : 'unchecked'}
                  onCheckedChange={() => toggleOwner(ownerCalls)}
                  className={someChecked ? 'opacity-60' : ''}
                  data-testid={`crm-owner-cb-${owner}`}
                />
                <Users className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="text-sm font-semibold text-gray-800 flex-1 truncate">{owner}</span>
                <span className="text-xs text-gray-400">{ownerCalls.length - ownerExcluded}/{ownerCalls.length}</span>
                <button
                  onClick={() => setCollapsed(p => ({ ...p, [owner]: !p[owner] }))}
                  className="ml-1 text-gray-400 hover:text-gray-600"
                  data-testid={`crm-owner-collapse-${owner}`}
                >
                  {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                </button>
              </div>

              {!isCollapsed && ownerCalls.map((call) => {
                const checked = !excludedIds.has(call.id);
                const bu = call.business_unit;
                return (
                  <label
                    key={call.id}
                    className={`flex items-start gap-3 px-4 py-2.5 cursor-pointer transition-colors ${checked ? 'hover:bg-gray-50' : 'bg-gray-50/50 opacity-60 hover:opacity-80'}`}
                    data-testid={`crm-call-row-${call.id}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleCall(call.id)}
                      className="mt-0.5 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs text-gray-500 flex-shrink-0">{_fmtDate(call.call_start_time)}</span>
                        {bu && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-sky-100 text-sky-700">{bu}</span>
                        )}
                        {call.call_purpose && (
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase ${_statusBadge('completed')}`}>
                            {call.call_purpose}
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-medium text-gray-800 truncate mt-0.5">
                        {call.account_name || '(no account)'}
                      </div>
                      {call.notes && (
                        <div className="text-xs text-gray-500 truncate mt-0.5">{call.notes}</div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-4 flex-shrink-0">
        <Button variant="ghost" onClick={onBack} disabled={generating} className="gap-1">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <Button
          onClick={onGenerate}
          disabled={generating || totalSelected === 0}
          className="text-white gap-1"
          style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}
          data-testid="crm-select-generate-btn"
        >
          {generating
            ? <><Loader2 className="w-4 h-4 animate-spin" />Generating…</>
            : <><Sparkles className="w-4 h-4" />Generate Report ({totalSelected} calls)</>
          }
        </Button>
      </div>
    </div>
  );
}

// ─── Main modal ────────────────────────────────────────────────────────────
const CrmSalesReportModal = ({
  isOpen, onClose,
  calls, quotesData, phocasData,
  startDate, endDate, teamName,
  buTotals,
  callsOnly = false,
  phocasSectors = {},
  onCreated,
}) => {
  const [phase, setPhase] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [reportName, setReportName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [excludedIds, setExcludedIds] = useState(new Set());

  const periodLabel = phocasData?.periodLabel || null;
  const prevPeriodLabel = phocasData?.prevPeriodLabel || null;
  const phocasRowCount = phocasData?.rowCount || 0;

  useEffect(() => {
    if (!isOpen) return;
    setPhase(1);
    setGenerating(false);
    setExcludedIds(new Set());
    setNameTouched(false);
  }, [isOpen]);

  // Auto-set report name from period
  useEffect(() => {
    if (!isOpen || nameTouched) return;
    const parts = [];
    if (periodLabel) parts.push(periodLabel);
    else if (startDate) {
      const d = new Date(startDate + 'T00:00:00');
      parts.push(d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }));
    }
    parts.push('Full CRM Sales Report');
    setReportName(parts.join(' — '));
  }, [isOpen, periodLabel, startDate, nameTouched]);

  const handleClose = () => {
    if (generating) return;
    onClose();
  };

  const handleNext = () => setPhase(2);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const selectedCalls = calls.filter(c => !excludedIds.has(c.id));

      const payload = {
        calls: selectedCalls,
        quotes_data: quotesData || {},
        phocas_by_name: phocasData?.byName || {},
        phocas_prev_by_name: phocasData?.prevByName || {},
        phocas_sectors: phocasSectors || {},
        period_label: periodLabel || '',
        prev_period_label: prevPeriodLabel || '',
        start_date: startDate || '',
        end_date: endDate || '',
        name: reportName || '',
        team_name: teamName || '',
        bu_totals: buTotals || {},
        calls_only: callsOnly,
        // New: year-level data + product groups
        phocas_prev_year: phocasData?.prevYearByName || {},
        phocas_curr_year: phocasData?.currYearByName || {},
        phocas_product_groups: phocasData?.productGroupsByName || {},
      };

      const res = await api.post(`${API}/api/zoho/market-sector-reports/create`, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        timeout: 120000,
      });

      const report = res.data?.report;
      if (!report) throw new Error('Report missing from response');
      toast.success('Full CRM Sales Report created');
      onClose();
      onCreated?.(report);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Report failed: ${detail || 'Unknown error'}`);
      setGenerating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent
        className={`flex flex-col ${phase === 2 ? 'max-w-2xl' : 'max-w-lg'}`}
        style={{ maxHeight: '90vh' }}
        data-testid="crm-report-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-sky-600" />
            {phase === 1 ? 'Create Full CRM Sales Report' : 'Select Calls to Include'}
          </DialogTitle>
          <DialogDescription>
            {phase === 1
              ? 'Generate a comprehensive sales report combining CRM call data with Phocas financial metrics.'
              : `Review all calls for ${periodLabel || 'the selected period'}. Uncheck any to exclude from the report.`
            }
          </DialogDescription>
        </DialogHeader>

        {phase === 1 && (
          <FormPhase
            reportName={reportName}
            setReportName={setReportName}
            setNameTouched={setNameTouched}
            periodLabel={periodLabel}
            prevPeriodLabel={prevPeriodLabel}
            phocasRowCount={phocasRowCount}
            callCount={calls.length}
            callsOnly={callsOnly}
            generating={generating}
            onBack={handleClose}
            onNext={handleNext}
          />
        )}

        {phase === 2 && (
          <CallSelectionPhase
            calls={calls}
            excludedIds={excludedIds}
            setExcludedIds={setExcludedIds}
            periodLabel={periodLabel}
            generating={generating}
            onBack={() => setPhase(1)}
            onGenerate={handleGenerate}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CrmSalesReportModal;
